import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { User, Edit, Mail } from "lucide-react";
import { ConfidenceBadge } from "./ConfidenceBadge";
import axios from "axios";
// 29/09/2026 — date gg/mm/aaaa da un'unica fonte (qui c'era formatDateIT).
import { formatDate } from "../../lib/format";

// Cache comuni per evitare richieste multiple
let _municipalitiesCache = null;

/**
 * ClientDataCard - Displays and edits client information
 * Shows different fields based on client_type (private vs business)
 */
export function ClientDataCard({ 
  clientData, 
  isEditing, 
  onToggleEdit, 
  onUpdateField,
  clientExists,
  existingClient,
  matchReason,
  showEditButton = true,
  createAlias = false,
  onCreateAliasChange = null,
  fieldConfidence = {}
}) {
  const fc = (field) => fieldConfidence[`client_${field}`];
  const isBusiness = (clientData?.client_type === "business");
  
  // Municipality auto-complete state
  const [municipalities, setMunicipalities] = useState({});
  const [citySuggestions, setCitySuggestions] = useState([]);
  const cityRef = useRef(null);

  // 15/10/2026 — proposta di cognome e nome dal codice fiscale (stessa regola del CRM):
  // si compila solo se i due campi sono vuoti; se il CF non conferma, si avvisa di controllare
  const [sceltaNome, setSceltaNome] = useState("");
  useEffect(() => {
    if (isBusiness || !clientData?.name || clientData?.first_name || clientData?.last_name) return undefined;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get("/api/clients/separa-nome",
          { params: { name: clientData.name, cf: clientData.fiscal_code || "" } });
        const p = r.data?.proposta;
        if (p?.last_name && p?.first_name) {
          onUpdateField("last_name", p.last_name);
          onUpdateField("first_name", p.first_name);
          setSceltaNome(r.data.sicura
            ? "✔ Cognome e nome divisi in base al codice fiscale."
            : "⚠ Il codice fiscale non conferma la divisione: controlla cognome e nome.");
        }
      } catch { /* niente proposta: si compilano a mano */ }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBusiness, clientData?.name, clientData?.fiscal_code]);

  // Load municipalities
  useEffect(() => {
    const load = async () => {
      if (_municipalitiesCache) {
        setMunicipalities(_municipalitiesCache);
        return;
      }
      try {
        const res = await axios.get("/api/clients/municipalities");
        _municipalitiesCache = res.data;
        setMunicipalities(res.data);
      } catch (e) { /* silent */ }
    };
    load();
  }, []);

  // Close suggestions on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (cityRef.current && !cityRef.current.contains(e.target)) {
        setCitySuggestions([]);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleCityChange = (value) => {
    const upper = value.toUpperCase();
    onUpdateField("city", upper);
    if (upper.length >= 2 && Object.keys(municipalities).length > 0) {
      const search = upper.toLowerCase();
      const results = Object.keys(municipalities)
        .filter(c => c.toLowerCase().startsWith(search))
        .slice(0, 8);
      setCitySuggestions(results);
    } else {
      setCitySuggestions([]);
    }
  };

  const selectCity = (city) => {
    const data = municipalities[city];
    if (data) {
      onUpdateField("city", city);
      onUpdateField("cap", data.c);
      onUpdateField("province", data.p);
    }
    setCitySuggestions([]);
  };

  // Parse fiscal code and auto-fill birth data
  const parseCfAndFill = async (cf) => {
    try {
      const res = await axios.get(`/api/clients/parse-fiscal-code/${cf}`);
      if (res.data.valid) {
        if (res.data.birth_date) onUpdateField("birth_date", res.data.birth_date);
        if (res.data.birth_place) onUpdateField("birth_place", res.data.birth_place);
        if (res.data.birth_place_province || res.data.estero) {
          onUpdateField("birth_place_province", res.data.estero ? "EE" : res.data.birth_place_province);
        }
        if (res.data.gender) onUpdateField("gender", res.data.gender);
      }
    } catch (e) { /* silent */ }
  };
  
  return (
    <Card className="bg-card border-border">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <User className="h-5 w-5 text-blue-500" />
          Dati Cliente
          {clientExists && (
            <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full">
              Cliente esistente
            </span>
          )}
        </CardTitle>
        {showEditButton && (
          <Button variant="ghost" size="sm" onClick={onToggleEdit}>
            <Edit className="h-4 w-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {isEditing ? (
          <div className="grid grid-cols-2 gap-3">
            {/* Tipo Cliente - sempre visibile */}
            <div>
              <FieldLabel label="Tipo Cliente" confidence={fc("type")} />
              <Select
                value={clientData?.client_type || "private"}
                onValueChange={(value) => onUpdateField("client_type", value)}
              >
                <SelectTrigger data-testid="client-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">Persona Fisica</SelectItem>
                  <SelectItem value="business">Azienda</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Sesso - solo privato */}
            {!isBusiness && (
              <div>
                <FieldLabel label="Sesso" confidence={fc("gender")} />
                <Select
                  value={clientData?.gender || ""}
                  onValueChange={(value) => onUpdateField("gender", value)}
                >
                  <SelectTrigger data-testid="client-gender-select">
                    <SelectValue placeholder="--" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Maschio</SelectItem>
                    <SelectItem value="F">Femmina</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Nome */}
            <div>
              <FieldLabel label={isBusiness ? "Ragione Sociale" : "Nome e cognome (come sul documento)"} confidence={fc("name")} />
              <Input
                value={clientData?.name || ""}
                onChange={(e) => onUpdateField("name", e.target.value)}
                data-testid="client-name-input"
              />
            </div>

            {/* 15/10/2026 — cognome e nome separati anche nell'importazione */}
            {!isBusiness && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <FieldLabel label="Cognome" />
                  <Input
                    value={clientData?.last_name || ""}
                    onChange={(e) => onUpdateField("last_name", e.target.value.toUpperCase())}
                    data-testid="client-last-name-input"
                  />
                </div>
                <div>
                  <FieldLabel label="Nome" />
                  <Input
                    value={clientData?.first_name || ""}
                    onChange={(e) => onUpdateField("first_name", e.target.value.toUpperCase())}
                    data-testid="client-first-name-input"
                  />
                </div>
                {sceltaNome && (
                  <p className="col-span-2 text-xs text-muted-foreground">{sceltaNome}</p>
                )}
              </div>
            )}

            {/* CF / P.IVA */}
            <div>
              <FieldLabel label={isBusiness ? "Partita IVA" : "Codice Fiscale"} confidence={fc("fiscal_code")} />
              <Input
                value={clientData?.fiscal_code || ""}
                onChange={(e) => {
                  const upper = e.target.value.toUpperCase();
                  onUpdateField("fiscal_code", upper);
                  // Auto-parse CF when complete (16 chars for private)
                  if (!isBusiness && upper.length === 16 && !/^\d{11}$/.test(upper)) {
                    parseCfAndFill(upper);
                  }
                }}
                maxLength={isBusiness ? 11 : 16}
                className="uppercase font-mono"
                data-testid="client-fiscal-code-input"
              />
            </div>

            {/* Campi PRIVATO: nascita */}
            {!isBusiness && (
              <>
                <div>
                  <FieldLabel label="Data Nascita" confidence={fc("birth_date")} />
                  <Input
                    type="date"
                    value={clientData?.birth_date || ""}
                    onChange={(e) => onUpdateField("birth_date", e.target.value)}
                    data-testid="client-birth-date-input"
                  />
                </div>
                <div>
                  <FieldLabel label="Luogo Nascita" confidence={fc("birth_place")} />
                  <Input
                    value={clientData?.birth_place || ""}
                    onChange={(e) => onUpdateField("birth_place", e.target.value)}
                    data-testid="client-birth-place-input"
                  />
                </div>
                <div>
                  <FieldLabel label="Prov. Nascita" />
                  <Input
                    value={clientData?.birth_place_province || ""}
                    onChange={(e) => onUpdateField("birth_place_province", e.target.value.toUpperCase())}
                    maxLength={2}
                    className="uppercase"
                    data-testid="client-birth-province-input"
                  />
                </div>
              </>
            )}

            {/* Campi AZIENDA: referente, PEC, SDI */}
            {isBusiness && (
              <>
                <div>
                  <FieldLabel label="Referente" />
                  <Input
                    value={clientData?.referent || ""}
                    onChange={(e) => onUpdateField("referent", e.target.value)}
                    placeholder="Nome persona di riferimento"
                    data-testid="client-referent-input"
                  />
                </div>
                <div>
                  <FieldLabel label="PEC" />
                  <Input
                    type="email"
                    value={clientData?.pec || ""}
                    onChange={(e) => onUpdateField("pec", e.target.value)}
                    placeholder="azienda@pec.it"
                    data-testid="client-pec-input"
                  />
                </div>
                <div>
                  <FieldLabel label="Codice SDI" />
                  <Input
                    value={clientData?.sdi_code || ""}
                    onChange={(e) => onUpdateField("sdi_code", e.target.value.toUpperCase())}
                    maxLength={7}
                    className="uppercase"
                    placeholder="0000000"
                    data-testid="client-sdi-input"
                  />
                </div>
              </>
            )}

            {/* Contatti - sempre visibili */}
            <div>
              <FieldLabel label="Email" confidence={fc("email")} />
              <Input
                type="email"
                value={clientData?.email || ""}
                onChange={(e) => onUpdateField("email", e.target.value)}
                data-testid="client-email-input"
              />
            </div>
            <div>
              <FieldLabel label="Telefono" confidence={fc("phone")} />
              <Input
                value={clientData?.phone || ""}
                onChange={(e) => onUpdateField("phone", e.target.value)}
                data-testid="client-phone-input"
              />
            </div>
            <div className="col-span-2">
              <FieldLabel label="Indirizzo" confidence={fc("address")} />
              <Input
                value={clientData?.address || ""}
                onChange={(e) => onUpdateField("address", e.target.value)}
                data-testid="client-address-input"
              />
            </div>
            <div className="relative" ref={cityRef}>
              <FieldLabel label="Città" confidence={fc("city")} />
              <Input
                value={clientData?.city || ""}
                onChange={(e) => handleCityChange(e.target.value)}
                className="uppercase"
                autoComplete="off"
                data-testid="client-city-input"
              />
              {citySuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 z-50 bg-card border border-border rounded-md shadow-lg mt-1 max-h-48 overflow-y-auto">
                  {citySuggestions.map((city) => (
                    <div
                      key={city}
                      className="px-3 py-2 cursor-pointer hover:bg-accent text-sm"
                      onClick={() => selectCity(city)}
                    >
                      {city} ({municipalities[city]?.c || ""} - {municipalities[city]?.p || ""})
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <FieldLabel label="Provincia" confidence={fc("province")} />
              <Input
                value={clientData?.province || ""}
                onChange={(e) => onUpdateField("province", e.target.value.toUpperCase())}
                maxLength={2}
                className="uppercase"
                data-testid="client-province-input"
              />
            </div>
            <div>
              <FieldLabel label="CAP" confidence={fc("cap")} />
              <Input
                value={clientData?.cap || ""}
                onChange={(e) => onUpdateField("cap", e.target.value)}
                data-testid="client-cap-input"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            <ClientField label="Tipo" value={isBusiness ? "Azienda" : "Persona Fisica"} />
            <ClientField label={isBusiness ? "Ragione Sociale" : "Nome"} value={clientData?.name} confidence={fc("name")} />
            {!isBusiness && (clientData?.last_name || clientData?.first_name) && (
              <ClientField label="Cognome / Nome" value={`${clientData?.last_name || "?"} / ${clientData?.first_name || "?"}`} />
            )}
            <ClientField label={isBusiness ? "Partita IVA" : "Codice Fiscale"} value={clientData?.fiscal_code} mono confidence={fc("fiscal_code")} />
            
            {/* Campi privato */}
            {!isBusiness && (
              <>
                <ClientField label="Sesso" value={clientData?.gender === "M" ? "Maschio" : clientData?.gender === "F" ? "Femmina" : null} confidence={fc("gender")} />
                <ClientField label="Data Nascita" value={formatDate(clientData?.birth_date)} highlight="blue" confidence={fc("birth_date")} />
                <ClientField 
                  label="Luogo Nascita" 
                  value={clientData?.birth_place ? `${clientData.birth_place}${clientData.birth_place_province ? ` (${clientData.birth_place_province})` : ''}` : null} 
                  confidence={fc("birth_place")}
                />
              </>
            )}
            
            {/* Campi azienda */}
            {isBusiness && (
              <>
                <ClientField label="Referente" value={clientData?.referent} />
                <ClientField label="PEC" value={clientData?.pec} />
                <ClientField label="Codice SDI" value={clientData?.sdi_code} mono />
              </>
            )}
            
            <ClientField label="Email" value={clientData?.email} confidence={fc("email")} />
            <ClientField label="Telefono" value={clientData?.phone} confidence={fc("phone")} />
            <ClientField label="Indirizzo" value={clientData?.address} confidence={fc("address")} />
            <ClientField 
              label="Città" 
              value={clientData?.city ? `${clientData.city}${clientData?.province ? ` (${clientData.province})` : ''} ${clientData?.cap || ''}` : null} 
              confidence={fc("city")}
            />
          </div>
        )}
        
        {clientExists && existingClient && (
          <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-sm" data-testid="client-exists-warning">
            <p className="text-amber-800 dark:text-amber-200 font-medium">
              Cliente già presente: <strong>{existingClient.name}</strong>
              {matchReason === "fiscal_code" && existingClient.fiscal_code && (
                <span className="text-xs ml-1">(CF: {existingClient.fiscal_code})</span>
              )}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {matchReason === "fiscal_code" 
                ? "Corrispondenza trovata tramite codice fiscale. La polizza verrà collegata a questo cliente."
                : "Corrispondenza trovata tramite nome. La polizza verrà collegata a questo cliente."
              }
            </p>
          </div>
        )}
        
        {/* Email Alias Option - only show for new clients */}
        {!clientExists && onCreateAliasChange && (
          <div className="mt-3 p-3 rounded-lg bg-muted/40 border">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="create_alias_import"
                data-testid="create-alias-import-checkbox"
                checked={createAlias}
                onChange={(e) => onCreateAliasChange(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <div className="flex-1">
                <Label htmlFor="create_alias_import" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                  <Mail className="h-4 w-4 text-primary" />
                  Crea Email Alias
                </Label>
                <p className="text-xs text-muted-foreground">
                  Genera un indirizzo di inoltro automatico per questo cliente
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Helper: Label with confidence badge for edit mode
function FieldLabel({ label, confidence }) {
  return (
    <Label className="text-xs flex items-center gap-1.5">
      {label}
      <ConfidenceBadge level={confidence} />
    </Label>
  );
}

// Helper component for displaying a single field with confidence
function ClientField({ label, value, mono = false, highlight = null, confidence }) {
  const valueClass = [
    mono && "font-mono",
    highlight === "blue" && "text-blue-600",
    !value && "text-muted-foreground"
  ].filter(Boolean).join(" ");

  return (
    <div className="flex justify-between items-center">
      <span className="text-muted-foreground flex items-center gap-1.5">
        {label}:
        <ConfidenceBadge level={confidence} />
      </span>
      <span className={valueClass || "font-medium"}>{value || "-"}</span>
    </div>
  );
}

export default ClientDataCard;
